import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { EmailVerificationService } from '@core/services/email-verification.service';

/**
 * "Verify your email" reminder (#943).
 *
 * Shown across the dashboard to an email/password account that has not
 * clicked its verification link. Nothing is blocked: the account can pay and
 * scan. Verification is needed only to invite teammates and export reports.
 * Dismissible for the rest of the browser session; it disappears for good
 * once the address is verified.
 */
@Component({
  selector: 'app-email-verification-banner',
  templateUrl: './email-verification-banner.component.html',
  styleUrl: './email-verification-banner.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailVerificationBannerComponent implements OnInit {
  private verification = inject(EmailVerificationService);
  private cdr = inject(ChangeDetectorRef);

  /** Whether the banner is on screen. */
  visible = false;
  /** The address the link goes to. */
  email: string | null = null;
  /** Result of the last resend, for the status line. */
  status: 'idle' | 'sending' | 'sent' | 'failed' | 'checking' = 'idle';

  ngOnInit(): void {
    if (!this.verification.isUnverified() || this.verification.isBannerDismissed()) {
      return;
    }
    this.email = this.verification.email;
    this.visible = true;
    // The link may have been clicked in another tab since sign-in.
    void this.verification.refresh().then((verified) => {
      if (verified) {
        this.visible = false;
        this.cdr.markForCheck();
      }
    });
  }

  /** Sends a new verification email. */
  async resend(): Promise<void> {
    this.status = 'sending';
    this.cdr.markForCheck();
    this.status = (await this.verification.resend()) ? 'sent' : 'failed';
    this.cdr.markForCheck();
  }

  /** Re-checks after the user says they clicked the link. */
  async checkAgain(): Promise<void> {
    this.status = 'checking';
    this.cdr.markForCheck();
    const verified = await this.verification.refresh();
    this.visible = !verified;
    this.status = 'idle';
    this.cdr.markForCheck();
  }

  /** Hides the banner for this browser session. */
  dismiss(): void {
    this.verification.dismissBanner();
    this.visible = false;
    this.cdr.markForCheck();
  }
}
